import type { ContentHref, ContentType } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectContentItem,
  selectPathToRoot,
  selectWebPage,
} from '@newStore/documentApi/documentApiSelectors';
import type { AsideTab } from '@newStore/documentUI/documentUIState';
import {
  selectAudienceTabForNode,
  selectEditConfigForNode,
  selectWebTabForNode,
} from '@newStore/documentUI/nodeTypeConfigSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import {
  getHighestValidationStatus,
  getValidationStatus,
} from '@newStore/validation/validationHelpers';
import { selectValidationResultForAsideNode } from '@newStore/validation/validationSelectors';
import type {
  ValidationErrorWithNode,
  ValidationStatus,
} from '@newStore/validation/validationTypes';
import type { EditAsideNamedSet, EditComponent } from '@nodeTypeConfig/configTypes';
import { get } from 'lodash';

export const selectCurrentEditingNode = (state: RootState) =>
  selectContentItem(state, state.documentUI.currentEditingNode as ContentHref);

export const selectCurrentWebConfig = (state: RootState) =>
  selectWebPage(state, state.documentUI.currentEditingNode as ContentHref);

export const selectOptionsForAsideReferenceFrames = createTypedSelector(
  [
    (state, href) => state.referenceFrames[href]?.content,
    (state, href, typesToFilter: ContentType[]) => typesToFilter,
  ],
  (referenceFrameContent, typesToFilter) => {
    if (!referenceFrameContent) {
      return [];
    }
    return Object.values(referenceFrameContent).filter((option) =>
      typesToFilter.includes(option.type),
    );
  },
);

const emptyArr = [];

export const selectInheritedPropertyValue = createTypedSelector(
  [
    (state, href: ContentHref) => selectPathToRoot(state, href),
    (state, href: ContentHref, config: EditComponent) =>
      config.property as EditAsideNamedSet['property'],
  ],
  (pathToRoot, property) => {
    // the first item is the current node, so we skip it with slice
    return (
      get(
        pathToRoot.slice(1).find((node) => get(node, property)),
        property,
      ) || emptyArr
    );
  },
);

export const selectCurrentOrInheritedPropertyValue = createTypedSelector(
  [
    (state, _href: ContentHref, config: EditComponent) =>
      'property' in config && selectCurrentEditingNode(state)?.[config.property],
    (state, href: ContentHref, config: EditComponent) =>
      selectInheritedPropertyValue(state, href, config),
  ],
  (currentValue, inheritedValue) => {
    // the value is either the currentValue of the editingNode, or the value of one of it parents (traversing up the tree)
    return currentValue || inheritedValue;
  },
);

export const selectAsideConfig = (state: RootState, href: ContentHref, asideTab: AsideTab) => {
  if (asideTab === 'metadata') {
    return selectEditConfigForNode(state, href);
  }
  if (asideTab === 'audiences') {
    return selectAudienceTabForNode(state, href);
  }
  if (asideTab === 'web') {
    return selectWebTabForNode(state, href);
  }
  return [];
};

const getHighestValidationStatusForAside = (
  validationErrors: ValidationErrorWithNode[] | null,
  editComponents: EditComponent[] | undefined,
): ValidationStatus => {
  if (!validationErrors || !editComponents) {
    return 'UNKNOWN';
  }
  const errorsForComponents = validationErrors.filter((error) =>
    editComponents.some(
      (component) =>
        error.component === component.component && error.property === component.property,
    ),
  );

  return getHighestValidationStatus([
    {
      validationErrors: errorsForComponents,
      status: getValidationStatus(errorsForComponents, true),
    },
  ]);
};

export const selectAsideHeaderTabValidationState = createTypedSelector(
  [
    (state, href: ContentHref, tab: AsideTab) => selectAsideConfig(state, href, tab),
    (state, href: ContentHref) => selectValidationResultForAsideNode(state, href),
  ],
  (asideConfig, validationErrors) => {
    const editConfigValidationStatus = getHighestValidationStatusForAside(
      validationErrors,
      asideConfig as EditComponent[] | undefined,
    );

    return editConfigValidationStatus;
  },
);

export const selectAsideHeaderTabVisible = createTypedSelector(
  [(state, href: ContentHref, tab: AsideTab) => selectAsideConfig(state, href, tab)],
  (asideConfig) => {
    return Boolean(asideConfig && asideConfig.length > 0);
  },
);
